import { ESCADA_MAX, PASSO_ESCADA_LABEL, type Oferta } from "../types";
import { fluxoDaOferta } from "../domain";
import { dataCurta } from "../lib/format";

/**
 * Linha do tempo da escada (P0.4): a evolução do passo ao longo das ofertas.
 * Área que "enche" em verde suave — comunica progresso, não nota. Cada ponto é
 * uma oferta confirmada; o último é destacado. Calmo, sem eixos barulhentos.
 *
 * Overlay casa vs terapia: registros de terapia (origem profissional) recebem
 * um marcador em ardósia, pra ver de relance se o que acontece na terapia se
 * reproduz em casa. A linha/área seguem a trajetória geral (cronológica).
 */
const W = 320;
const H = 110;
const PAD_X = 14;
const PAD_T = 14;
const PAD_B = 22;

export function EscadaTimeline({ ofertas }: { ofertas: Oferta[] }) {
  const pts = ofertas
    .filter((o) => o.confirmado)
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora));

  const x = (i: number) =>
    pts.length <= 1
      ? W / 2
      : PAD_X + (i * (W - 2 * PAD_X)) / (pts.length - 1);
  const y = (passo: number) =>
    PAD_T + (1 - passo / ESCADA_MAX) * (H - PAD_T - PAD_B);
  const baseline = y(0);

  const ultimo = pts[pts.length - 1];
  const rotulo =
    pts.length === 0
      ? "sem ofertas ainda"
      : `evolução em ${pts.length} ${pts.length === 1 ? "oferta" : "ofertas"}, até "${PASSO_ESCADA_LABEL[ultimo.passo_escada]}"`;

  if (pts.length === 0) {
    return (
      <div className="timeline-vazio">a linha do tempo aparece após a 1ª oferta.</div>
    );
  }

  const linha = pts.map((o, i) => `${x(i)},${y(o.passo_escada)}`).join(" ");
  const area = `${PAD_X},${baseline} ${linha} ${x(pts.length - 1)},${baseline}`;
  const temTerapia = pts.some((o) => fluxoDaOferta(o) === "terapia");

  return (
    <>
    <svg
      className="timeline"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={rotulo}
    >
      {/* faixas de referência suaves (passos 0,3,6,9) */}
      {[0, 3, 6, 9].map((p) => (
        <line
          key={p}
          x1={PAD_X}
          x2={W - PAD_X}
          y1={y(p)}
          y2={y(p)}
          className="timeline-grade"
        />
      ))}

      {pts.length > 1 && <polygon points={area} className="timeline-area" />}
      <polyline points={linha} className="timeline-linha" />

      {pts.map((o, i) => {
        const ehUltimo = i === pts.length - 1;
        const cx = x(i);
        const cy = y(o.passo_escada);
        // terapia: losango em ardósia (distinção por forma, não só por cor)
        if (fluxoDaOferta(o) === "terapia") {
          const s = ehUltimo ? 5.5 : 4;
          return (
            <rect
              key={o.id}
              x={cx - s}
              y={cy - s}
              width={s * 2}
              height={s * 2}
              transform={`rotate(45 ${cx} ${cy})`}
              className={ehUltimo ? "timeline-ponto terapia ultimo" : "timeline-ponto terapia"}
            />
          );
        }
        return (
          <circle
            key={o.id}
            cx={cx}
            cy={cy}
            r={ehUltimo ? 5 : 3.5}
            className={ehUltimo ? "timeline-ponto ultimo" : "timeline-ponto"}
          />
        );
      })}

      {/* datas da 1ª e última oferta */}
      <text x={PAD_X} y={H - 6} className="timeline-data" textAnchor="start">
        {dataCurta(pts[0].data_hora)}
      </text>
      {pts.length > 1 && (
        <text x={W - PAD_X} y={H - 6} className="timeline-data" textAnchor="end">
          {dataCurta(ultimo.data_hora)}
        </text>
      )}
    </svg>
    {temTerapia && (
      <div className="timeline-legenda" aria-hidden>
        <span className="leg-item">
          <span className="leg-marca casa" /> em casa
        </span>
        <span className="leg-item">
          <span className="leg-marca terapia" /> terapia
        </span>
      </div>
    )}
    </>
  );
}
